import React from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { format } from 'date-fns';

const AutomationMonitor = ({ 
  automationEnabled, 
  onToggleAutomation, 
  lastSyncTime,
  onGenerateSlots,
  loading
}) => {
  return (
    <div className="bg-white rounded-xl shadow-elevation-1 p-6 mb-6">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-foreground flex items-center gap-2 mb-2">
            <Icon name="Zap" size={20} className="text-primary" />
            Automated Slot Generation Monitor
          </h2>
          <p className="text-sm text-muted-foreground">
            Monitor and control automated Monday slot generation system
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-muted-foreground">Automation:</span>
            <button
              onClick={onToggleAutomation}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                automationEnabled ? 'bg-green-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  automationEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
            <span className={`text-sm font-semibold ${
              automationEnabled ? 'text-green-600' : 'text-gray-500'
            }`}>
              {automationEnabled ? 'Enabled' : 'Disabled'}
            </span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Integration Status */}
        <div className="bg-gradient-to-br from-green-50 to-teal-50 rounded-lg p-4 border border-green-200">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <Icon name="CheckCircle" size={20} className="text-green-600" />
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground">Integration Status</p>
              <p className="text-xs text-muted-foreground">System connection</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            <span className="text-sm font-medium text-green-700">Active & Connected</span>
          </div>
        </div>

        {/* Last Sync */}
        <div className="bg-gradient-to-br from-[#E6F0F5] to-purple-50 rounded-lg p-4 border border-[#002B45]/20">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-[#D1E7ED] rounded-lg flex items-center justify-center">
              <Icon name="RefreshCw" size={20} className="text-[#002B45]" />
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground">Last Sync</p>
              <p className="text-xs text-muted-foreground">Data synchronization</p>
            </div>
          </div>
          <p className="text-sm font-medium text-[#002B45]">
            {lastSyncTime ? format(lastSyncTime, 'MMM dd, h:mm a') : 'Never'}
          </p>
        </div>

        {/* Error Logging */}
        <div className="bg-gradient-to-br from-gray-50 to-slate-50 rounded-lg p-4 border border-gray-200">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
              <Icon name="AlertCircle" size={20} className="text-gray-600" />
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground">Error Logging</p>
              <p className="text-xs text-muted-foreground">System diagnostics</p>
            </div>
          </div>
          <p className="text-sm font-medium text-gray-700">No errors detected</p>
        </div>
      </div>

      {/* Manual Generation Controls */}
      <div className="mt-6 pt-6 border-t border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-1">
              Manual Slot Generation
            </h3>
            <p className="text-xs text-muted-foreground">
              Generate Monday slots for the next 4 weeks (10:00 AM, 11:30 AM, 2:00 PM, 3:30 PM)
            </p>
          </div>
          <Button
            variant="default"
            onClick={onGenerateSlots}
            iconName="Play"
            iconPosition="left"
            loading={loading}
            disabled={loading}
          >
            Generate Monday Slots
          </Button>
        </div>
      </div>

      {/* Generation Pattern Info */}
      <div className="mt-4 bg-[#E6F0F5] border border-[#002B45]/20 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Icon name="Info" size={16} className="text-[#002B45] flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-[#002B45] mb-1">Automated Generation Pattern</p>
            <p className="text-xs text-[#002B45]/80">
              System automatically generates Monday consultation slots at fixed times: 10:00 AM - 11:00 AM,
              11:30 AM - 12:30 PM, 2:00 PM - 3:00 PM, and 3:30 PM - 4:30 PM for the next 4 consecutive Mondays.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AutomationMonitor;